import { Dialog } from "@headlessui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Badge from "../../components/badges/Badges";
import { FormBox, ModalFoterBox, ModalHeaderBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Label, TextField } from "../../components/fields/Fields";
import FormController from "../../components/fields/FormController";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";
import { APP_USE, GetAppUseStateFromUserData, UserData, userSchema } from "../../model/Users";
import { SelectFields } from "../../components/select/SelectFields";
import companiesQuery from "../../firestore/Companies.query";
import { checkOption, isOptionActive } from "../../controller/OptionController";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import { useDispatch } from "react-redux";

export type UserProps = {
  user: UserData;
  toggleGroupSelect: () => void;
  toggleFamilyEmail: () => void;
  onSubmit: (data: UserData) => Promise<void>;
  isVoid: boolean;
  companyDisplayId: string;
  familyEmails?: string[];
  onSearchKey1Select: (id: string) => void;
  searchKey1Selected: string | undefined;
  searchKey1List: { value: string; disabled: boolean; }[];
  onSearchKey2Select: (id: string) => void;
  searchKey2Selected: string | undefined;
  searchKey2List: { value: string; disabled: boolean; }[];
  onSearchKey3Select: (id: string) => void;
  searchKey3Selected: string | undefined;
  searchKey3List: { value: string; disabled: boolean; }[];
  tenantId: string;
}

export const UserEditModal = ({
  open,
  toggle,
  toggleGroupSelect,
  toggleFamilyEmail,
  onSubmit,
  isVoid,
  closeOnClickOutside,
  user,
  companyDisplayId,
  familyEmails,
  onSearchKey1Select,
  searchKey1Selected,
  searchKey1List,
  onSearchKey2Select,
  searchKey2Selected,
  searchKey2List,
  onSearchKey3Select,
  searchKey3Selected,
  searchKey3List,
  tenantId,
}: ModalProp & UserProps) => {

  const [sexSelected, setSexSelected] = useState<number | undefined>(user.sex);
  const schema = userSchema(tenantId);
  const dispatch = useDispatch();

  const { control, handleSubmit, setValue, reset, formState: { errors }} =
    useForm<UserData>({
      resolver: yupResolver(schema),
      defaultValues: user,
    });
  

  const adminFlgItem: RadioButtonItem[] = [
    {
      id: "adminTrue",
      title: "利用可",
    },
    {
      id: "adminFalse",
      title: "利用不可",
    },
  ];

  const deleteFlgItem: RadioButtonItem[] = [
    {
      id: "deleteFalse",
      title: "利用可",
    },
    {
      id: "deleteTrue",
      title: "利用不可",
    },
    {
      id: "suspensionTrue",
      title: "利用停止",
    },
  ];

  
  const pointAssignmentAuthorityItem: RadioButtonItem[] = [
    {
      id: "pointAssignmentAuthorityTrue",
      title: "利用可",
    },
    {
      id: "pointAssignmentAuthorityFalse",
      title: "利用不可",
    },
  ];

  const sexItem = [
    {
      value: undefined,
      name: "",
    },
    {
      value: 0,
      name: "男",
    },
    {
      value: 1,
      name: "女",
    },
    {
      value: 2,
      name: "その他",
    },
  ];

  const admin = useInput(user.admin);
  const deleteFlg = useInput(user.deleteFlg);
  const pointAssignmentAuthority = useInput(user.pointAssignmentAuthority);
  const accountSuspensionFlg = useInput(user.accountSuspensionFlg);
  const [isPointManagementEnabled, setIsPointManagementEnabled] = useState(admin.value === 'adminTrue');
  const [optionFlg, setOptionFlg] = useState<{ point: boolean }>({
    point: false
  });
  
  useEffect(() => {
    const appUse = GetAppUseStateFromUserData(user);

    setValue("id", user.id);
    setValue("tenantId", user.tenantId);
    setValue("lastName", user.lastName);
    setValue("lastNameKana", user.lastNameKana);
    setValue("birthDate", user.birthDate);
    setValue("groups", user.groups);
    setValue("familyEmail", user.familyEmail);
    setValue("phoneNumber", user.phoneNumber);
    setValue("faxNumber", user.faxNumber);
    setValue("iconURL", user.iconURL);
    setValue("decryptedPasscode", user.decryptedPasscode);
    setValue("note", user.note ?? '');
    setValue("zipCode", user.zipCode ?? '');
    setValue("address", user.address ?? '');
    setSexSelected(user.sex);

    admin.setValue(user.admin ? "adminTrue" : "adminFalse");
    deleteFlg.setValue(appUse === APP_USE.SUSPEND ? "suspensionTrue" : appUse === APP_USE.UNUSEABLE
      ? "deleteTrue" : "deleteFalse");
      pointAssignmentAuthority.setValue(user.pointAssignmentAuthority === null || user.pointAssignmentAuthority === undefined 
        ? null 
        : user.pointAssignmentAuthority === true 
          ? "pointAssignmentAuthorityTrue" 
          : "pointAssignmentAuthorityFalse");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[setValue, user]);

  useEffect(() => {
    setValue("sex", sexSelected);
    setValue("admin", (admin.value === "adminTrue"));
    setValue("deleteFlg", (deleteFlg.value === "deleteTrue"));
    setValue("accountSuspensionFlg", (deleteFlg.value === "suspensionTrue"))
    setValue("searchKey1", searchKey1Selected);
    setValue("searchKey2", searchKey2Selected);
    setValue("searchKey3", searchKey3Selected);
    setValue(
      "pointAssignmentAuthority", 
      pointAssignmentAuthority.value === "pointAssignmentAuthorityTrue" 
        ? true 
        : (pointAssignmentAuthority.value === "pointAssignmentAuthorityFalse" ? false : null)
    );
  },[admin.value, deleteFlg.value, accountSuspensionFlg.value, pointAssignmentAuthority.value, setValue, sexSelected, searchKey1Selected, searchKey2Selected, searchKey3Selected])

  // キャンセルの処理
  useEffect(() => {
    if(!open) {
      reset();
      const appUse = GetAppUseStateFromUserData(user);
      admin.setValue(user.admin ? "adminTrue" : "adminFalse");
      deleteFlg.setValue(appUse === APP_USE.UNUSEABLE ? "deleteTrue" : "deleteFalse");
      accountSuspensionFlg.setValue(appUse === APP_USE.SUSPEND ? "suspensionTrue" : "");
      pointAssignmentAuthority.setValue(user.pointAssignmentAuthority === true 
        ? "pointAssignmentAuthorityTrue" 
        : user.pointAssignmentAuthority === false 
          ? "pointAssignmentAuthorityFalse" 
          : "");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open, reset, user.admin, user.deleteFlg, user.accountSuspensionFlg, user.pointAssignmentAuthority]);

  const onSexSelect = (value: number) => {
    setSexSelected(value);
  }

  //オプション設定出し分け
  async function fetchOptions() {
    const options = await companiesQuery.getOptionContractList(tenantId!);
    // ポイント管理のステータス、期間チェック
    const pointOption = options.find((option) => option.optionId === 'point');
    const point = pointOption ? isOptionActive(pointOption) : false;
    return { point };
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await fetchOptions();
      setOptionFlg(result);
    }
  
    loadOptions();
  }, []);

  // 運営者アプリの変更ハンドラー
  useEffect(() => {
    if (admin.value === "adminTrue") {
      setIsPointManagementEnabled(true);
    } else {
      setIsPointManagementEnabled(false);
      setValue("pointAssignmentAuthority", false);
    }
  }, [admin.value, setValue]);

  useEffect(() => {
    // ポイント管理が無効の場合、利用者のポイント管理を「利用不可」に更新する
    if (!optionFlg["point"]) {
      setValue("pointAssignmentAuthority", false);
    }
  }, [optionFlg]);

  const onValidSubmit = async (data: UserData) => {
    if (data.pointAssignmentAuthority) {
      const pointEnabled = await checkOption("point", dispatch, false);
      if (!pointEnabled) {
        dispatch(
          alertOpen(
            AlertValue(
              "ポイント管理のオプションが無効のため、オプション設定のポイント管理を利用可に設定できません。",
              "danger"
            )
          )
        );
        return;
      }
    }
    await onSubmit(data);
  };

  return (<> 
    {GetAppUseStateFromUserData(user) === APP_USE.SUSPEND ?
      (<Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all sm:w-full max-w-screen-sm ">
          <form>
            <div className="px-2 pb-4 sm:p-4">
              <div className="text-left sm:mt-3">
                <ModalHeaderBox
                  title={"編集できません"}
                />
                <div className="text-left pl-5 mt-3 ">
                  アプリ利用が「停止」の利用者のため、編集できません。
                </div>
              </div>
            </div>

            <ModalFoterBox>
              <Button
                className="mx-2 mt-1"
                onClick={toggle}
                children={"OK"}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </Modal>)

      : (<Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all w-[1300px]">
          <form
            className=""
            onSubmit={handleSubmit(onValidSubmit)}
            method="POST"
            noValidate
          >
            <div className="px-2 pb-4 sm:p-4">
              <div className="text-center mt-3">
                <ModalHeaderBox
                  title={`利用者番号: ${user.id.substring(user.id.indexOf(companyDisplayId) + companyDisplayId.length)}`}
                  Subtitle={`利用者情報を入力してください`}
                />

              </div>
              <div className="flex gap-4">
                <div className="w-1/2">
                  <FormBox gridcol={"grid-cols-3"}>
                    <FormController
                      control={control}
                      name={"lastName"}
                      label={"名前"}
                      type={"text"}
                      required
                    />
                    <FormController
                      control={control}
                      name={"lastNameKana"}
                      label={"フリガナ"}
                      type={"text"}
                    />
                    <FormController
                      control={control}
                      name={"birthDate"}
                      label={"生年月日"}
                      type={"date"}
                    />
                
                    <Controller
                      name={"sex"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <SelectFields
                          {...field}
                          label="性別"
                          color="indigo"
                          onChange={(e: any) => {onSexSelect(e.target.value);}}
                          value={sexSelected}
                          className={"h-16 col-span-1"}
                          selectItems={sexItem?.map((item) => {
                            return {
                              name: item.name,
                              value: item.value,
                            }
                          })}
                          error={fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                     <div className="col-span-2"></div>
                  </FormBox>

                  <FormBox gridcol={"grid-cols-4"}>
                    <FormController
                      control={control}
                      name={"zipCode"}
                      label={"郵便番号"}
                      type={"text"}
                      className={"col-span-1"}
                    />
                    <FormController
                      control={control}
                      name={"address"}
                      label={"住所"}
                      type={"text"}
                      className={"col-span-3"}
                    />
                  </FormBox>

                  <FormBox gridcol={"grid-cols-3"}>
                    <FormController
                      control={control}
                      name={"phoneNumber"}
                      label={"電話番号"}
                      type={"text"}
                    />
                    <FormController
                      control={control}
                      name={"faxNumber"}
                      label={"FAX番号"}
                      type={"text"}
                    />
                  </FormBox>

                  <FormBox gridcol={"grid grid-cols-3"}> 
                    <RadioButton
                      label={"運営者アプリ"}
                      value={admin}
                      name="admin"
                      radioButtonItem={adminFlgItem}
                      required
                      children={undefined}
                    />
                    <RadioButton
                      label={"アプリ利用"}
                      children={undefined}
                      value={deleteFlg}
                      name={"deleteFlg"}
                      radioButtonItem={deleteFlgItem}
                      required
                      className={"col-span-2 w-80"}
                    />
                  </FormBox>

                  {optionFlg.point && (
                    <FormBox gridcol={"grid grid-cols-1 pt-5 h-[130px] max-w-[300px] relative"}>
                      <div
                        className={`z-0 overflow-hidden h-[100px] shadow ring-1 ring-black ring-opacity-5 md:rounded-lg ${!isPointManagementEnabled ? 'bg-gray-100' : ''}`}
                      >
                        <span className={`absolute z-10 top-[10px] left-[12px] text-sm text-gray-900 ${!isPointManagementEnabled ? 'bg-gray-100' : 'bg-white'}`}>オプション設定</span>
                        <RadioButton
                          label={"ポイント管理"}
                          value={pointAssignmentAuthority}
                          name={"pointManagement"}
                          radioButtonItem={pointAssignmentAuthorityItem}
                          required
                          className={"col-span-1 p-3 pt-4"}
                          disabled={!isPointManagementEnabled}
                          children={undefined}
                        /> 
                        {errors.pointAssignmentAuthority && (
                            <p className="text-red-500 text-xs mb-1 ml-3">{errors.pointAssignmentAuthority.message}</p>
                          )}
                      </div>
                    </FormBox>
                  )}
          
              </div>
                <div className="w-1/2">
                  <FormBox>
                    <div>
                      <Label id={"001"}>所属グループ</Label>
                      {isVoid}
                      {user.groups.map(
                        (group, index) => (
                          <Badge
                            key={`${group}${index}${user.id}`}
                            color={group.color}
                            className={group.disabled ? "opacity-30" : ""}>
                            {group.name}
                          </Badge>
                        ))}
                    </div>
                    <div className="flex justify-end">
                    <Button
                      size="sm"
                      className={"self-end w-48"}
                      onClick={toggleGroupSelect}
                      children={"グルーブ選択"}
                    />
                    </div>
                 
                  </FormBox>
                  <FormBox>
                    <div>
                      <Label id={"familyEmail"}>イベント参加履歴メール配信先</Label>
                      <ul>
                      {
                        familyEmails?.map((email, index) => {
                          return (<li key={`${email}${index}`}>{email}</li>)
                        })
                      }
                      </ul>
                    </div>
                    <div className="flex justify-end">
                    <Button
                      size="sm"
                      className={"self-end w-64"}
                      onClick={toggleFamilyEmail}
                      children={"メールアドレス登録"}
                    />
                    </div>
                  </FormBox>
    
                  <FormBox gridcol={"grid-cols-3"}>
                    <Controller
                      name={"searchKey1"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <SelectFields
                          {...field}
                          label="検索キー1"
                          color="indigo"
                          onChange={(e: any) => {onSearchKey1Select(e.target.value);}}
                          value={searchKey1Selected}
                          className={"h-16"}
                          selectItems={searchKey1List?.map((item) => {
                            return {
                              name: item.value,
                              value: item.value,
                              disabled: item.disabled,
                            }
                          })}
                          error={fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                    <Controller
                      name={"searchKey2"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <SelectFields
                          {...field}
                          label="検索キー2"
                          color="indigo"
                          onChange={(e: any) => {onSearchKey2Select(e.target.value);}}
                          value={searchKey2Selected}
                          className={"h-16"}
                          selectItems={searchKey2List?.map((item) => {
                            return {
                              name: item.value,
                              value: item.value,
                              disabled: item.disabled,
                            }
                          })}
                          error={fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                    <Controller
                      name={"searchKey3"}
                      control={control}
                      render={({ field, fieldState }) => (
                        <SelectFields
                          {...field}
                          label="検索キー3"
                          color="indigo"
                          onChange={(e: any) => {onSearchKey3Select(e.target.value);}}
                          value={searchKey3Selected}
                          className={"h-16"}
                          selectItems={searchKey3List?.map((item) => {
                            return {
                              name: item.value,
                              value: item.value,
                              disabled: item.disabled,
                            }
                          })}
                          error={fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </FormBox>

                  <FormBox gridcol="grid-cols-1" className={"h-auto pb-6"}>
                    <Controller
                      name="note"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="textarea"
                          label="備考"
                          className="h-full"
                          error={fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </FormBox>
                </div>
              </div>
            </div>

            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[132px]"
                onClick={toggle}
                outline
                children={"キャンセル"}
              />
              <Button
                className="mt-1 w-[132px]" submit
                children={"登録"}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </Modal>)
    }
  </>);
}